import { useState } from "react";
import produce from "immer";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import { Label, OptionGroup } from "components/Styled";
import ButtonWithLoading from "components/ButtonWithLoading";
import StorageAccountSelector from "components/StorageAccounts/StorageAccountsSelector";
import { canConfirm, canImpersonate, showSecurityOptionsIn } from "./utils";
import { Storage, StorageProvider } from "@dashboard-v3/api";
import { getCloudStorage } from "pages/Rules/Form/context/ruleSelectors";
import { RuleUrlParams } from "pages/Rules/common/types";
import AsUserSelector from "./AsUserSelector";
import SelectSecurityOptions from "./SelectSecurityOptions";
import { SecurityOption } from "./types";
import { ContentWrapper } from "./Styled";
import { useRuleContext } from "../../context/ruleContext";

import { StorageAccountUpdate } from "components/StorageAccounts/types";

interface StorageAccountProps {
  securityOptions: SecurityOption[];
}

type WarningState = {
  isOpen: boolean;
  data: StorageAccountUpdate;
};

export default function StorageAccount({
  securityOptions,
}: StorageAccountProps) {
  const { t } = useTranslation("rules");
  const { state, dispatch } = useRuleContext();
  const { rule } = state;
  const { action }: RuleUrlParams = useParams();
  const storage: Storage = getCloudStorage(rule);
  const [warningState, setWarningState] = useState<WarningState>(initWarning());

  function updateStorageAccount(update: StorageAccountUpdate) {
    const { authentication, provider, userId } = update;

    const changes = produce(storage, draft => {
      if (authentication === "INDIVIDUAL") {
        draft.userIds = [];
        draft.asUsers = provider === "DROPBOX" ? draft.asUsers : [];
      }

      draft.provider = provider;
      draft.authentication = authentication;
      draft.asUser = null;
      draft.userId = userId || "";
      draft.storeIdUserId = null;
      draft.storeId = null;

      if (draft.location.parentId) {
        delete draft.location.parentId;
        delete draft.location.parentName;
      }

      if (provider === "MXHERO_STORAGE") {
        draft.userIds = [];
        draft.asUsers = [];
        draft.userId = "";

        // default security option set
        draft.linkSecurity = {
          id: "OPEN_LINKS_7_DAYS",
          type: "DEFINITION",
        };

        // Do not override option is disabled for "MXHERO_STORAGE" provider,
        // always sets override to default storage value
        // See: web/src/pages/Rules/Form/context/initStorage.ts
        draft.override = true;
      } else {
        draft.linkSecurity = null;
      }
    });

    dispatch({ type: "checkFolderCollaboration", payload: { provider } });

    dispatch({
      type: "updateStep",
      payload: { step: "cloudStorage", changes },
    });
  }

  function handleStorageAccountChange(update: StorageAccountUpdate) {
    if (showIndStorageWarning(storage, update)) {
      return setWarningState({ isOpen: true, data: update });
    }

    updateStorageAccount(update);
  }

  const showAsUserSelector = () => {
    const { type } = state;

    if (type === "ZIP_ATTACHMENTS") return false;

    return canImpersonate(storage);
  };

  return (
    <ContentWrapper>
      {state.type === "ZIP_ATTACHMENTS" ? (
        <OptionGroup>
          <Label wording={t("cloudStorage.accounts.title")} />
          <Typography variant="body2">
            {t(`cloudStorage.accounts.org.${storage.provider}`)}
          </Typography>
        </OptionGroup>
      ) : (
        <OptionGroup>
          <Label wording={t("cloudStorage.accounts.title")} />
          <StorageAccountSelector
            label={t("cloudStorage.accounts.label")}
            allowOrgAccounts
            isEditing={Boolean(state.rule.id) && action === "edit"}
            disabled={state.rule.actionType === "DRAG_AND_DROP"}
            onChange={handleStorageAccountChange}
            value={{
              authentication: storage.authentication,
              provider: storage.provider,
              userId: storage.userId,
            }}
          />
        </OptionGroup>
      )}
      {showAsUserSelector() && <AsUserSelector key={storage.provider} />}
      {showSecurityOptionsIn("STEP", state.rule) && (
        <SelectSecurityOptions options={securityOptions} />
      )}
      <ButtonWithLoading
        variant="contained"
        disableElevation
        fullWidth
        size="large"
        loading={state.asyncStatus === "validate_storage_access"}
        disabled={!canConfirm(state)}
        onClick={() => {
          dispatch({ type: "confirmStep", payload: "cloudStorage" });
        }}
      >
        {t("confirm")}
      </ButtonWithLoading>

      <Dialog open={warningState.isOpen}>
        <DialogTitle>{t("cloudStorage.individualWarning.title")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t("cloudStorage.individualWarning.desc")}
          </Typography>
        </DialogContent>
        <DialogActions style={{ marginTop: 10 }}>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            disableElevation
            onClick={() => setWarningState(initWarning())}
          >
            {t("common:cancel")}
          </Button>
          <Button
            variant="contained"
            size="small"
            color="primary"
            disableElevation
            onClick={() => {
              updateStorageAccount(warningState.data);
              setWarningState(initWarning());
            }}
          >
            {t("common:continue")}
          </Button>
        </DialogActions>
      </Dialog>
    </ContentWrapper>
  );
}

function initWarning(): WarningState {
  return { isOpen: false, data: null };
}

function showIndStorageWarning(storage: Storage, update: StorageAccountUpdate) {
  const { authentication: previousAuth } = storage;
  const { authentication, provider } = update;
  const orgProviders: StorageProvider[] = ["EGNYTE", "EGNYTE_GOV", "DROPBOX"];

  if (previousAuth === "INDIVIDUAL" && orgProviders.includes(provider)) {
    return false;
  }

  if (authentication === "INDIVIDUAL" && !orgProviders.includes(provider)) {
    return true;
  }

  return false;
}
